import { WhitelabelSource } from '../enums/whitelabel-source';
import colorConvert from 'color-convert';
import { IFaviconResource, WhitelabelConfigModel } from '../models/whitelabel-config/whitelabel-config.model';
import { injectPushlySdk } from './pushly-sdk';
import aqe from '@pushly/aqe';

const removeFaviconResource = (resource: Element) => {
    document.head.removeChild(resource);
};

const createFaviconResource = (resourceConfig: IFaviconResource) => {
    const link = document.createElement('link');
    link.rel = resourceConfig.rel;
    link.href = resourceConfig.href;

    document.head.append(link);
};

const replaceFaviconResources = (config: WhitelabelConfigModel): void => {
    const resources = config.getFaviconResources();

    if (resources?.length > 0) {
        const selectors = 'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]';
        const links = document.head.querySelectorAll(selectors);

        links.forEach(removeFaviconResource);
        resources.forEach(createFaviconResource);
    }
};

export const setGlobalCssVariables = (config: WhitelabelConfigModel): void => {
    const root = document.querySelector(':root') as HTMLElement ?? document.documentElement;

    const setColorOverride = (hex: string, type: 'primary' | 'secondary' | 'tertiary') => {
        const rgb = colorConvert.hex.rgb(hex);
        const hsl = colorConvert.hex.hsl(hex);

        root.style.setProperty(`--color-${type}-100`, hex);
        root.style.setProperty(`--color-${type}-100-rgb`, `${rgb[0]}, ${rgb[1]}, ${rgb[2]}`);
        root.style.setProperty(`--color-${type}-100-r`, rgb[0].toString());
        root.style.setProperty(`--color-${type}-100-g`, rgb[1].toString());
        root.style.setProperty(`--color-${type}-100-b`, rgb[2].toString());
        root.style.setProperty(`--color-${type}-100-hsl`, `${hsl[0]}, ${hsl[1]}%, ${hsl[2]}%`);
        root.style.setProperty(`--color-${type}-100-h`, hsl[0].toString());
        root.style.setProperty(`--color-${type}-100-s`, `${hsl[1].toString()}%`);
        root.style.setProperty(`--color-${type}-100-l`, `${hsl[2].toString()}%`);

        console.debug(`Setting ${type} color override`);
    };

    if (config.getPrimaryColorHex()) {
        setColorOverride(config.getPrimaryColorHex()!, 'primary');
    }

    if (config.getSecondaryColorHex()) {
        setColorOverride(config.getSecondaryColorHex()!, 'secondary');
    }

    if (config.getTertiaryColorHex()) {
        setColorOverride(config.getTertiaryColorHex()!, 'tertiary');
    }

    if (config.getLoginBackgroundUrl()) {
        root.style.setProperty('--login-background-url', `url("${config.getLoginBackgroundUrl()}")`);
        console.debug('Setting login media override');
    }

    if (config.getLogoUrl()) {
        root.style.setProperty('--logo-url', `url("${config.getLogoUrl()}")`);
        console.debug('Setting logo override');
    }

    if (config.getPrimaryIconUrl()) {
        root.style.setProperty('--primary-icon-url', `url("${config.getPrimaryIconUrl()}")`);
        console.debug('Setting primary icon override');
    }

    if (config.getSidebarIconUrl()) {
        root.style.setProperty('--sidebar-icon-url', `url("${config.getSidebarIconUrl()}")`);
        console.debug('Setting sidebar-v2 icon override');
    }
}

export const loadWhitelabelSettings = async (): Promise<WhitelabelConfigModel | undefined> => {
    const hostname = location.hostname;

    // Fallback to using pushly platform domain
    let domainKey = process.env.PLATFORM_DOMAIN_KEY;

    return new Promise<WhitelabelConfigModel | undefined>(async res => {
        try {
            const settingsRes = await fetch(`${aqe.defaults.publicCdnDomain}/platform-settings/${hostname}`, {});

            if (settingsRes.ok) {
                const data = await settingsRes.json();
                const model = WhitelabelConfigModel.build(data);
                domainKey = model.getPreviewDomainKey() ?? domainKey;

                if (model.getIsActive()) {
                    console.info(`Loading platform settings for ${hostname}`)

                    // start dom updates
                    document.title = model.getDisplayName();
                    document.body.classList.add('whitelabeled');
                    setGlobalCssVariables(model);
                    replaceFaviconResources(model);

                    res(model);
                } else {
                    res(undefined);
                }
            } else {
                res(undefined);
            }
        } catch (err) {
            res(undefined);
        }

        // start sdk initialization
        injectPushlySdk(domainKey!);
    });
};
